export interface IEventDispatcher<D extends IEventDispatcher<D>, E = {}> {
  events: E
}

interface EventListener<D extends IEventDispatcher<D>, T> {
  (event: Event<D, T>, data?: T): void
  executeOnce?: boolean
}

interface IEventCanceler {
  (): void
  cancel(): void
}

interface GlobalEvents {
  dispatched: Event<typeof Event, { event: Event<any, any>, data: any }>
}

export default class Event<D extends IEventDispatcher<D>, T> {
  static events: GlobalEvents = {
    dispatched: new Event(Event, 'dispatched')
  }

  private listeners = new Set<EventListener<D, T>>()
  paused: boolean = false

  constructor(public dispatcher: D, public name: string) { }

  static listen(listener: (event: Event<any, any>, data: any) => void) {
    return Event.events.dispatched.add(listener)
  }

  static clearListeners() {
    return Event.events.dispatched.deleteAll()
  }

  static list<E = {}, D extends IEventDispatcher<D> = IEventDispatcher<any>>(dispatcher: D, ...eventNames: (keyof E)[]) {
    const e: any = {}
    eventNames.forEach(name => {
      e[name] = new Event<D, any>(dispatcher, name as string)
    })
    return e as E
  }

  add(listener: EventListener<D, T>): IEventCanceler {
    this.listeners.add(listener)
    const cancel = () => this.delete(listener)
    return Object.assign(cancel, { cancel })
  }

  addOnce(listener: EventListener<D, T>): IEventCanceler {
    listener.executeOnce = true
    return this.add(listener)
  }

  delete(listener: EventListener<D, T>) {
    this.listeners.delete(listener)
  }

  pause() {
    this.paused = true
  }

  resume() {
    this.paused = false
  }

  deleteAll() {
    this.listeners.forEach(listener => this.delete(listener))
  }

  dispatch(data?: T) {
    if (this.paused) return
    if (this as Event<any, any> === Event.events.dispatched) return

    Event.events.dispatched.dispatch({ event: this, data })

    this.listeners.forEach(listener => {
      listener(this, data)
      if (listener.executeOnce)
        this.delete(listener)
    })
  }
}
